export type GpsPacePoint = { t: number; dist: number };

/** Live pace from speed (m/s) → sec/km. Near-stopped returns 0. */
export function instantPaceFromSpeedMps(speedMps: number | undefined | null): number {
  if (speedMps == null || !Number.isFinite(speedMps) || speedMps < 0.5) return 0;
  const pace = 1000 / speedMps;
  return pace > 0 && pace <= 900 ? pace : 0;
}

/** Live pace over the latest ~2–3s of a distance/time window. */
export function livePaceFromWindow(
  window: GpsPacePoint[],
  maxAgeMs = 3000,
): number {
  if (window.length < 2) return 0;
  const last = window[window.length - 1];
  const cutoff = last.t - maxAgeMs;
  let first = last;
  for (let i = window.length - 1; i >= 0; i--) {
    if (window[i].t >= cutoff) first = window[i];
    else break;
  }
  const distKm = (last.dist - first.dist) / 1000;
  const timeSec = (last.t - first.t) / 1000;
  if (distKm < 0.003 || timeSec < 1) return 0;
  const pace = timeSec / distKm;
  return pace > 0 && pace <= 900 ? pace : 0;
}

/**
 * Live pace over the latest ~25–50m of movement — smoother than a pure time window,
 * more responsive than last-kilometer.
 */
export function livePaceFromDistanceWindow(
  window: GpsPacePoint[],
  targetMeters = 40,
  minMeters = 25,
): number {
  if (window.length < 2) return 0;
  const last = window[window.length - 1];
  let first = last;
  for (let i = window.length - 1; i >= 0; i--) {
    first = window[i];
    if (last.dist - window[i].dist >= targetMeters) break;
  }
  const distMeters = last.dist - first.dist;
  if (distMeters < minMeters) return 0;
  const timeSec = (last.t - first.t) / 1000;
  if (timeSec < 4) return 0;
  const pace = timeSec / (distMeters / 1000);
  return pace > 0 && pace <= 900 ? pace : 0;
}

/** Append a same-distance sample so standing still ages the window without a GPS callback. */
export function ageGpsPaceWindow(
  window: GpsPacePoint[],
  nowMs = Date.now(),
): GpsPacePoint[] {
  if (window.length === 0) return window;
  const last = window[window.length - 1];
  if (nowMs - last.t > 800) {
    window.push({ t: nowMs, dist: last.dist });
  }
  return window.filter((p) => p.t >= nowMs - 90_000);
}

export function metersMovedSince(
  window: GpsPacePoint[],
  maxAgeMs: number,
  nowMs = Date.now(),
): number {
  if (window.length === 0) return 0;
  const last = window[window.length - 1];
  const cutoff = nowMs - maxAgeMs;
  let first = last;
  for (let i = window.length - 1; i >= 0; i--) {
    if (window[i].t >= cutoff) first = window[i];
    else break;
  }
  return Math.max(0, last.dist - first.dist);
}

/**
 * Instant/rolling current pace — never falls back to average.
 * Stationary (no movement in ~4s) → 0 so the UI shows --:-- like Runkeeper.
 */
export function resolveCurrentLivePace(args: {
  watchLive: boolean;
  watchLivePace: number;
  watchRollingPace: number;
  gpsWindow: GpsPacePoint[];
  lastGpsSpeedMps: number;
  lastGpsSpeedAt: number;
  nowMs?: number;
}): number {
  const nowMs = args.nowMs ?? Date.now();
  const window = ageGpsPaceWindow(args.gpsWindow, nowMs);

  if (args.watchLive) {
    if (args.watchLivePace > 0 && args.watchLivePace <= 900) return args.watchLivePace;
    if (args.watchRollingPace > 0 && args.watchRollingPace <= 900) return args.watchRollingPace;
  }

  const moved = metersMovedSince(window, 4000, nowMs);
  const speedPace =
    nowMs - args.lastGpsSpeedAt < 3000
      ? instantPaceFromSpeedMps(args.lastGpsSpeedMps)
      : 0;
  // No recent movement and no fresh GPS speed → standing still.
  if (moved < 3 && speedPace <= 0) return 0;

  const distanceWindowPace = livePaceFromDistanceWindow(window);
  const rollingPace = livePaceFromWindow(window, 3000);
  let livePace = 0;
  if (distanceWindowPace > 0) livePace = distanceWindowPace;
  else if (speedPace > 0) livePace = speedPace;
  else if (rollingPace > 0) livePace = rollingPace;
  if (livePace > 900) livePace = 0;
  return livePace;
}

/** Whole-run average pace (elapsed / distance). Returns 0 until ~10 m. */
export function averagePaceSecondsPerKm(elapsedSeconds: number, distanceMeters: number): number {
  const km = distanceMeters / 1000;
  if (!(km > 0.01) || !(elapsedSeconds > 0)) return 0;
  const pace = elapsedSeconds / km;
  return pace > 0 && pace <= 900 ? pace : 0;
}
